/**
 * 「いま打つ位置」に下線を引くテキスト(参考画像の緑の下線)。
 *
 * どちらも要素の textContent は元の文字列のまま変わらない
 * (e2e が `.romaji-rest` を読んで打鍵しているので、ここは崩せない)。
 *
 * 使い分け:
 *  - `CursorText`  … 文字列そのものが1打ごとに変わるところ(ローマ字ガイドの「残り」)。
 *                    3ノードに割るだけなので、毎回作り直しても安い。
 *  - `CursorChars` … 文字列は変わらず位置だけ動くところ(ふりがな・ひらがなの問題文)。
 *                    1文字1spanで置いておき、下線の位置決めだけを更新する。
 *                    テキストノードを書き換えないので、1打ごとの再レイアウトが起きない
 *                    (CLAUDE.md ルール6 の入力遅延50ms)。
 *
 * 下線そのものは文字spanの `::after` ではなく、**独立した絶対配置のバー**(`.cursor-bar`)。
 * `.type-cursor` の位置を測って `transform: translate3d()` で置くので、
 * 隣の文字へ移るときに瞬間移動ではなく横へスッと滑る(CSS transition / styles.css)。
 * 折返しで行が変わるときは Y も一緒に動く。
 */

import { Fragment, useLayoutEffect, useRef, type ReactNode } from 'react';

interface Props {
  readonly text: string;
  /** 下線を引く文字の位置(0始まり)。範囲外なら下線を出さない。 */
  readonly activeIndex: number;
  /** 半角スペースの表示(ローマ字ガイドは「␣」にする)。 */
  readonly spaceChar?: string;
  /**
   * 「同じ出題の続き」を見分ける値。変わった瞬間だけ下線をワープさせる(滑らせない)。
   * 省略時は `text` を使う。ローマ字ガイドのように text が1打ごとに変わるところでは、
   * 出題ごとに一定な値(打った分 + 残り)を渡すこと。
   */
  readonly resetKey?: string;
}

/** 下線の左右の余白(文字幅に対する比率)。旧 `::after` の `left/right: 4%` と同じ。 */
const INSET_RATIO = 0.04;

/**
 * 下線バーを `.type-cursor` の真下に置く。
 *
 * 読み取り(getBoundingClientRect)→ 書き込み(style)の順に1回ずつしか触らないので、
 * 1打鍵あたりの強制レイアウトは1回で済む。
 *
 * @param jump true ならアニメーションさせずに即座に移す(出題の切り替え・リサイズ時)。
 */
function place(bar: HTMLElement, jump: boolean): void {
  const host = bar.parentElement;
  // 絶対配置の基準 = 位置指定された祖先。styles.css で `.word-panel > p` を relative にしてある。
  const base = bar.offsetParent;
  const target = host?.querySelector<HTMLElement>('.type-cursor') ?? null;
  if (!(base instanceof HTMLElement) || !target) {
    bar.style.opacity = '0';
    return;
  }
  const baseRect = base.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  const barHeight = bar.offsetHeight;
  // jsdom など実測できない環境では 0 が返る。下線を出さずに抜ける。
  if (rect.width === 0 && rect.height === 0) {
    bar.style.opacity = '0';
    return;
  }
  const inset = rect.width * INSET_RATIO;
  // 文字ボックスの**中**(下端)に収める。下へはみ出させると段落の scrollHeight が増え、
  // FitText の縮小ループが誤作動して文字が小さくなる(長文e2e の「縦に切れている」判定)。
  const x = rect.left - baseRect.left + inset;
  const y = rect.bottom - baseRect.top - barHeight;

  if (jump) bar.style.transitionDuration = '0s';
  bar.style.width = `${Math.max(rect.width - inset * 2, 0)}px`;
  bar.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  bar.style.opacity = '1';
  if (jump) {
    // 0s のまま一度反映させてから戻す。こうしないと次の打鍵まで transition が効かない。
    void bar.offsetWidth;
    bar.style.transitionDuration = '';
  }
}

function CursorBar({ resetKey }: { readonly resetKey: string }): ReactNode {
  const ref = useRef<HTMLSpanElement | null>(null);
  const lastKey = useRef<string | null>(null);

  // 依存配列を付けない。親が再描画されるたび(= 1打鍵ごと)に測り直す。
  useLayoutEffect(() => {
    const bar = ref.current;
    if (!bar) return;
    const jump = lastKey.current !== resetKey;
    lastKey.current = resetKey;
    place(bar, jump);
  });

  useLayoutEffect(() => {
    const bar = ref.current;
    const base = bar?.offsetParent;
    if (!bar || !(base instanceof HTMLElement) || typeof ResizeObserver === 'undefined') return;
    // FitText の実測縮小・画面リサイズで文字の位置が変わったら置き直す。
    // ResizeObserver のコールバックはレイアウト後・描画前に走るので、ズレた1フレームは出ない。
    const observer = new ResizeObserver(() => place(bar, true));
    observer.observe(base);
    return () => observer.disconnect();
  }, []);

  return <span ref={ref} className="cursor-bar" aria-hidden="true" />;
}

export function CursorText({ text, activeIndex, spaceChar, resetKey }: Props): ReactNode {
  const show = (value: string): string =>
    spaceChar === undefined ? value : value.replace(/ /g, spaceChar);

  const chars = [...text];
  const key = resetKey ?? text;
  // 下線を出さないときもバーは置いたままにする(消えるだけ。次の出題で作り直さない)。
  if (activeIndex < 0 || activeIndex >= chars.length) {
    return (
      <Fragment>
        {show(text)}
        <CursorBar resetKey={key} />
      </Fragment>
    );
  }

  const before = chars.slice(0, activeIndex).join('');
  const current = chars[activeIndex] ?? '';
  const after = chars.slice(activeIndex + 1).join('');
  return (
    <Fragment>
      {show(before)}
      <span className="type-cursor">{show(current)}</span>
      {show(after)}
      <CursorBar resetKey={key} />
    </Fragment>
  );
}

export function CursorChars({ text, activeIndex, resetKey }: Omit<Props, 'spaceChar'>): ReactNode {
  return (
    <Fragment>
      {[...text].map((char, index) => (
        <span key={index} className={index === activeIndex ? 'type-cursor' : undefined}>
          {char}
        </span>
      ))}
      <CursorBar resetKey={resetKey ?? text} />
    </Fragment>
  );
}
